import { CommandInput } from "cmdk";

import IconButton from "@/components/Button/IconButton";
import Icon from "@/components/Icon/Icon";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

import type { DeviceMultiSelectComboboxProps, DeviceOption } from "./-types";
import DeviceMultiSelectOption from "./DeviceMultiSelectOption";
import DeviceMultiSelectOptionChip from "./DeviceMultiSelectOptionChip";

export function DeviceMultiSelectCombobox({
  options,
  selectedDeviceOptions,
  onChange,
  searchText = "",
  searchPlaceholder = "",
  isPending = false,
  onSearchChange,
}: DeviceMultiSelectComboboxProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.Combobox.deviceMultiSelectCombobox",
  });

  const handleSearchChange = (text: string) => {
    onSearchChange?.(text);
  };

  const handleSelect = (option: DeviceOption) => {
    if (selectedDeviceOptions.find((opt) => opt.value === option.value)) {
      onChange(
        selectedDeviceOptions.filter((opt) => opt.value !== option.value),
      );
    } else {
      onChange([...selectedDeviceOptions, option]);
    }
    handleSearchChange("");
  };

  const handleClearAll = () => {
    onChange([]);
    handleSearchChange("");
  };

  return (
    <Command shouldFilter={false}>
      <div className="flex min-h-[80px] gap-2 rounded-md border border-neutral-muted-border px-3 py-2">
        <div className="flex flex-wrap flex-1 gap-1 items-start py-2">
          {selectedDeviceOptions.map((selected) => (
            <DeviceMultiSelectOptionChip
              key={selected.id}
              option={selected}
              onRemove={() => handleSelect(selected)}
              disabled={selected.disabled}
            />
          ))}
          <CommandInput
            className="w-full h-6 text-xs outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={searchPlaceholder}
            value={searchText}
            onValueChange={handleSearchChange}
          />
        </div>
        {selectedDeviceOptions.length > 0 && (
          <div className="content-center h-10">
            <IconButton
              onClick={handleClearAll}
              icon="XOctagonIcon"
              variant="ghost"
              size="Default"
              aria-label={t("clearAllDeviceOptions")}
            />
          </div>
        )}
      </div>
      <CommandList
        className={cn(
          "gap-1 p-1 max-h-[200px]",
          options.length > 0 && "shadow-xs", // 候補がある時のみshadowを表示
          isPending && "opacity-50",
        )}
      >
        {options
          .filter(
            (option) =>
              !selectedDeviceOptions.some((opt) => opt.value === option.value),
          )
          .map((option) => (
            <CommandItem
              key={option.id}
              value={option.id}
              onSelect={() => handleSelect(option)}
              disabled={option.disabled}
              className={cn(
                "cursor-pointer gap-1 px-1 py-1",
                option.disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <DeviceMultiSelectOption device={option} />
            </CommandItem>
          ))}
        {isPending && (
          <div className="flex justify-center py-2">
            <Icon
              iconName="Loader2Icon"
              size={24}
              className="opacity-50 animate-spin"
            />
          </div>
        )}
      </CommandList>
    </Command>
  );
}

export type { DeviceMultiSelectComboboxProps, DeviceOption } from "./-types";
